import React from 'react';
import { Table as AntTable } from 'antd';
import cpa from 'utils/cpa';
import formatCurrency from 'utils/formatCurrency';
import { GlobalPerformanceItem } from '../types/performance.types';

export type VendorWithColor = GlobalPerformanceItem & {
  color?: string;
};

interface GlobalVendorsCommonTableProps {
  columns: any[];
  dataSource: VendorWithColor[];
  loading?: boolean;
  rowKey?: string | ((record: VendorWithColor) => string);
  pagination?: any;
  size?: 'small' | 'middle' | 'large';
}

export const getColorBasedOnPercentage = (percentage: number): string => {
  const roundedPercentage = Math.round(percentage * 100) / 100;
  if (roundedPercentage >= 0.75) {
    return 'green';
  } else if (roundedPercentage >= 0.65 && roundedPercentage <= 0.74) {
    return '#FFDB58';
  } else {
    return 'red';
  }
};

export const calculatePtc = (data: VendorWithColor): number => {
  const premium = (data.totalAncillariesPremium || 0) + (data.totalPrivateMedPremium || 0);
  const cost = data.totalCost || 0;
  if (cost === 0) return 0;
  return premium / cost;
};

export const formatPtc = (value: number): string => `${value.toFixed(2)}x`;

export const createGlobalVendorsColumns = (showColorIndicator: boolean = false) => {
  const vendorNameRender = (_: any, record: VendorWithColor) => (
    <div>
      {showColorIndicator && (
        <span
          style={{
            display: 'inline-block',
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: record.color,
            marginRight: '10px',
          }}
        />
      )}
      <span>{record.globalName}</span>
    </div>
  );

  return [
    {
      title: 'Vendor Name',
      dataIndex: 'globalName',
      key: 'globalName',
      render: vendorNameRender,
    },
    {
      title: 'CPA-A',
      dataIndex: 'CPA-A',
      key: 'CPA-A',
      render: (_: any, record: VendorWithColor) => {
        const { formatted } = cpa(record.totalCost, record.totalU65Sales);
        return <span>{formatted}</span>;
      },
      sorter: (a: VendorWithColor, b: VendorWithColor) => {
        const { value: cpaA } = cpa(a.totalCost, a.totalU65Sales);
        const { value: cpaB } = cpa(b.totalCost, b.totalU65Sales);
        if (cpaA === null || cpaB === null) return 0;
        return cpaA - cpaB;
      },
    },
    {
      title: 'CPA-B',
      dataIndex: 'CPA-B',
      key: 'CPA-B',
      render: (_: any, record: VendorWithColor) => {
        const totalSales = record.totalACASales + record.totalU65Sales;
        const { formatted } = cpa(record.totalCost, totalSales);
        return <span>{formatted}</span>;
      },
      sorter: (a: VendorWithColor, b: VendorWithColor) => {
        const totalSalesA = a.totalACASales + a.totalU65Sales;
        const totalSalesB = b.totalACASales + b.totalU65Sales;
        const { value: cpaA } = cpa(a.totalCost, totalSalesA);
        const { value: cpaB } = cpa(b.totalCost, totalSalesB);
        if (cpaA === null || cpaB === null) return 0;
        return cpaA - cpaB;
      },
    },
    {
      title: 'Sales',
      dataIndex: 'sales',
      key: 'sales',
      render: (_: any, record: VendorWithColor) => record.totalU65Sales + record.totalACASales,
      sorter: (a: VendorWithColor, b: VendorWithColor) =>
        (a.totalU65Sales + a.totalACASales) - (b.totalU65Sales + b.totalACASales),
    },
    {
      title: 'ACA',
      dataIndex: 'totalACASales',
      key: 'totalACASales',
      render: (text: number) => text || 0,
      sorter: (a: VendorWithColor, b: VendorWithColor) => (a.totalACASales || 0) - (b.totalACASales || 0),
    },
    {
      title: 'U65',
      dataIndex: 'totalU65Sales',
      key: 'totalU65Sales',
      render: (text: number) => text || 0,
      sorter: (a: VendorWithColor, b: VendorWithColor) => (a.totalU65Sales || 0) - (b.totalU65Sales || 0),
    },
    {
      title: 'Ancillaries',
      dataIndex: 'totalAncillaries',
      key: 'totalAncillaries',
      render: (text: number) => text || 0,
      sorter: (a: VendorWithColor, b: VendorWithColor) => (a.totalAncillaries || 0) - (b.totalAncillaries || 0),
    },
    {
      title: 'Q Call',
      dataIndex: 'totalBillableLeads',
      key: 'totalBillableLeads',
      render: (text: number) => text || 0,
      sorter: (a: VendorWithColor, b: VendorWithColor) => (a.totalBillableLeads || 0) - (b.totalBillableLeads || 0),
    },
    {
      title: 'CR',
      dataIndex: 'cr',
      key: 'cr',
      render: (_: any, record: VendorWithColor) => {
        const rate = (total: number, fraction: number) => {
          if (total === 0) return 0;
          return Math.round((fraction / total) * 100);
        };
        const totalSales = record.totalACASales + record.totalU65Sales;
        return `${rate(record.totalBillableLeads, totalSales)}%`;
      },
      sorter: (a: VendorWithColor, b: VendorWithColor) => {
        const rate = (total: number, fraction: number) => {
          if (total === 0) return 0;
          return Math.round((fraction / total) * 100);
        };
        const totalSalesA = a.totalACASales + a.totalU65Sales;
        const totalSalesB = b.totalACASales + b.totalU65Sales;
        return rate(a.totalBillableLeads, totalSalesA) - rate(b.totalBillableLeads, totalSalesB);
      },
    },
    {
      title: 'PTC',
      dataIndex: 'PTC',
      key: 'PTC',
      render: (_: any, record: VendorWithColor) => {
        const ptcValue = calculatePtc(record);
        const color = getColorBasedOnPercentage(ptcValue);
        return <span style={{ color }}>{formatPtc(ptcValue)}</span>;
      },
      sorter: (a: VendorWithColor, b: VendorWithColor) => calculatePtc(a) - calculatePtc(b),
    },
    {
      title: 'Core-AP',
      dataIndex: 'coreAp',
      key: 'coreAp',
      render: (_: any, record: VendorWithColor) => {
        const totalU65Sales = record.totalU65Sales || 0;
        return totalU65Sales > 0 ? formatCurrency(record.totalPrivateMedPremium / totalU65Sales) : 'N/A';
      },
      sorter: (a: VendorWithColor, b: VendorWithColor) => {
        const totalU65A = a.totalU65Sales || 0;
        const totalU65B = b.totalU65Sales || 0;
        if (totalU65A === 0 || totalU65B === 0) return 0;
        return (a.totalPrivateMedPremium / totalU65A) - (b.totalPrivateMedPremium / totalU65B);
      },
    },
    {
      title: 'Total Premium',
      dataIndex: 'totalPremium',
      key: 'totalPremium',
      render: (_: any, record: VendorWithColor) => {
        const premium = (record.totalPrivateMedPremium || 0) + (record.totalAncillariesPremium || 0);
        return formatCurrency(premium);
      },
      sorter: (a: VendorWithColor, b: VendorWithColor) => {
        const premiumA = (a.totalPrivateMedPremium || 0) + (a.totalAncillariesPremium || 0);
        const premiumB = (b.totalPrivateMedPremium || 0) + (b.totalAncillariesPremium || 0);
        return premiumA - premiumB;
      },
    },
    {
      title: 'C-RPC',
      dataIndex: 'crpc',
      key: 'crpc',
      render: (_: any, record: VendorWithColor) => {
        const cpaValue = cpa(record.totalCost, record.totalU65Sales);
        const sales = record.totalU65Sales + record.totalACASales;
        const qCalls = record.totalBillableLeads || 0;
        if (cpaValue.value === null || qCalls === 0) return 'No Data';
        const rpc = (cpaValue.value * sales) / qCalls;
        return formatCurrency(rpc);
      },
      sorter: (a: VendorWithColor, b: VendorWithColor) => {
        const cpaA = cpa(a.totalCost, a.totalU65Sales);
        const salesA = a.totalU65Sales + a.totalACASales;
        const qCallsA = a.totalBillableLeads || 0;
        const rpcA = cpaA.value === null || qCallsA === 0 ? 0 : (cpaA.value * salesA) / qCallsA;

        const cpaB = cpa(b.totalCost, b.totalU65Sales);
        const salesB = b.totalU65Sales + b.totalACASales;
        const qCallsB = b.totalBillableLeads || 0;
        const rpcB = cpaB.value === null || qCallsB === 0 ? 0 : (cpaB.value * salesB) / qCallsB;

        return rpcA - rpcB;
      },
    },
  ];
};

const GlobalVendorsCommonTable: React.FC<GlobalVendorsCommonTableProps> = ({
  columns,
  dataSource,
  loading = false,
  rowKey = (record) => record.globalId,
  pagination,
  size = 'small',
}) => {
  return (
    <AntTable
      columns={columns}
      dataSource={dataSource}
      rowKey={rowKey}
      loading={loading}
      pagination={pagination}
      size={size}
    />
  );
};

export default GlobalVendorsCommonTable;
